import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamPeriod, ParamProjectSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { ensureIssueWithinProjectConstraint } from "./issue-D65xI0F7.js";
import { hasAgentProvider } from "./provider-factory-Cs_2DBhe.js";
import { withProviderFallback } from "./provider-fallback-L61Pbfxx.js";
import { formatErrorResults } from "./formatters-Cuh2gD9M.js";
import { RECOMMENDED_FIELDS } from "./config-ChOUIglS.js";
import { parseIssueParams } from "./utils-_4r1K2PE.js";
import { searchIssueEventsAgent } from "./agent-Bbb5dUlD.js";
import { z } from "zod";
import { getActiveSpan, setTag } from "@sentry/core";

//#region src/tools/catalog/search-issue-events.ts
function buildIssueEventSearchRepairPrompt(params) {
	return [
		"Fix this Sentry issue event search request.",
		"The query may be natural language or already-valid Sentry event search syntax.",
		"Preserve valid explicit parameters, but correct query syntax, fields, sort, and time range when they conflict or would fail.",
		"",
		`User query: ${params.query || "(empty)"}`,
		"Current parameters:",
		JSON.stringify({
			sort: params.sort ?? null,
			statsPeriod: params.statsPeriod ?? null
		}, null, 2)
	].join("\n");
}
var search_issue_events_default = defineTool({
	name: "search_issue_events",
	skills: ["inspect", "triage"],
	requiredScopes: ["event:read"],
	description: [
		"Search and filter events within a specific issue.",
		"",
		"Provide `query` as natural language or Sentry event search syntax. When an embedded agent is configured, it fixes filters, fields, sort, and time range before running.",
		"",
		"The tool automatically constrains results to the specified issue.",
		"",
		"Common Query Filters:",
		"- environment:production - Filter by environment",
		"- release:1.0.0 - Filter by release version",
		"- user.email:alice@example.com - Filter by user",
		"- trace:TRACE_ID - Filter by trace ID",
		"",
		"For cross-issue searches use search_issues. For single issue or event details use get_sentry_resource.",
		"",
		"<examples>",
		"search_issue_events(issueId='MCP-41', organizationSlug='my-org', query='from last hour')",
		"search_issue_events(issueId='MCP-41', organizationSlug='my-org', query='environment:production')",
		"search_issue_events(issueUrl='https://sentry.io/.../issues/123/', query='release:v1.0.0', period='7d')",
		"</examples>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug.nullable().default(null).describe("Organization slug. Required when using issueId. Not needed when using issueUrl."),
		issueId: z.string().optional().describe("Issue ID (e.g., 'MCP-41', 'PROJECT-123'). Requires organizationSlug. Alternatively, use issueUrl."),
		issueUrl: z.string().url().optional().describe("Full Sentry issue URL (e.g., 'https://sentry.io/organizations/my-org/issues/123/'). Includes both organization and issue ID."),
		query: z.string().trim().optional().describe("Natural language or Sentry event search query syntax for filtering within the issue."),
		sort: z.string().optional().describe("Sort field (prefix with - for descending). Default: -timestamp"),
		period: ParamPeriod.optional(),
		projectSlug: ParamProjectSlug.nullable().default(null).describe("Project slug for better tag discovery. Optional - helps find project-specific tags."),
		regionUrl: ParamRegionUrl.nullable().default(null).describe("Sentry region URL. Optional - defaults to main region."),
		limit: z.number().min(1).max(100).default(50).describe("Maximum number of events to return (1-100, default: 50)"),
		includeExplanation: z.boolean().default(false).describe("Include explanation of how the query was translated or repaired")
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const { organizationSlug, issueId } = parseIssueParams({
			organizationSlug: params.organizationSlug,
			issueId: params.issueId,
			issueUrl: params.issueUrl
		});
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		setTag("organization.slug", organizationSlug);
		setTag("issue.id", issueId);
		if (params.projectSlug) setTag("project.slug", params.projectSlug);
		await ensureIssueWithinProjectConstraint({
			apiService,
			organizationSlug,
			issueId,
			projectSlug: context.constraints.projectSlug
		});
		let projectId;
		if (params.projectSlug) try {
			const project = await apiService.getProject({
				organizationSlug,
				projectSlugOrId: params.projectSlug
			});
			projectId = String(project.id);
		} catch (error) {
			console.warn(`Could not resolve project ${params.projectSlug}:`, error);
		}
		let query;
		let fields;
		let sortParam;
		let timeParams;
		let explanation;
		if (hasAgentProvider()) {
			const parsed = await withProviderFallback({
				operation: "search_issue_events.rewrite",
				fallback: () => ({
					query: params.query ?? "",
					fields: RECOMMENDED_FIELDS,
					sort: params.sort || "-timestamp",
					timeRange: { statsPeriod: params.period ?? "14d" },
					explanation: ""
				}),
				run: async () => (await searchIssueEventsAgent({
					query: buildIssueEventSearchRepairPrompt({
						query: params.query,
						sort: params.sort,
						statsPeriod: params.period
					}),
					organizationSlug,
					apiService,
					projectId
				})).result
			});
			if (!parsed.sort) throw new UserInputError(`Search Issue Events Agent response missing required 'sort' parameter. Received: ${JSON.stringify(parsed, null, 2)}. The agent must specify how to sort results (e.g., '-timestamp' for newest first).`);
			query = parsed.query || "";
			sortParam = parsed.sort;
			explanation = parsed.explanation;
			const requestedFields = parsed.fields || [];
			fields = requestedFields.length > 0 ? requestedFields : RECOMMENDED_FIELDS;
			timeParams = parsed.timeRange ? { ...parsed.timeRange } : { statsPeriod: "14d" };
		} else {
			query = params.query ?? "";
			fields = RECOMMENDED_FIELDS;
			sortParam = params.sort || "-timestamp";
			timeParams = { statsPeriod: params.period ?? "14d" };
		}
		const eventsResponse = await apiService.listEventsForIssue({
			organizationSlug,
			issueId,
			query,
			limit: params.limit,
			sort: sortParam,
			...timeParams
		});
		const explorerQuery = query ? `issue:${issueId} ${query}` : `issue:${issueId}`;
		const explorerUrl = apiService.getEventsExplorerUrl(organizationSlug, explorerQuery, projectId, "errors", fields, sortParam, [], [], timeParams.statsPeriod, timeParams.start, timeParams.end);
		function isValidEventArray(data) {
			return Array.isArray(data) && data.every((item) => typeof item === "object" && item !== null);
		}
		if (!isValidEventArray(eventsResponse)) throw new Error("Invalid event data format from Sentry API: expected array of objects");
		getActiveSpan()?.setAttribute("gen_ai.tool.call.result.count", eventsResponse.length);
		const naturalLanguageContext = params.query ? `Events in issue ${issueId}: ${params.query}` : `Events in issue ${issueId}`;
		return formatErrorResults({
			eventData: eventsResponse,
			inputQuery: naturalLanguageContext,
			includeExplanation: params.includeExplanation,
			apiService,
			organizationSlug,
			explorerUrl,
			sentryQuery: explorerQuery,
			fields,
			explanation
		});
	}
});

//#endregion
export { search_issue_events_default };
//# sourceMappingURL=search-issue-events-fYjr_r6r.js.map